import React, { useEffect, useState } from 'react';
import { userApi } from '../api/userApi';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ shain_code: '', role: 'user', password: '' });
  const [createdPassword, setCreatedPassword] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([userApi.list(), userApi.availableShain()]);
      setUsers(u);
      setAvailable(a);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setCreatedPassword('');
    try {
      const res = await userApi.create({
        shain_code: Number(form.shain_code),
        role: form.role,
        password: form.password || undefined,
      });
      setMessage(res.message);
      setCreatedPassword(res.password);
      setForm({ shain_code: '', role: 'user', password: '' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (u) => {
    const actionLabel = u.is_active ? '無効化' : '有効化';
    if (!window.confirm(`${u.display_name}（${u.login_id}）を${actionLabel}しますか？`)) {
      return;
    }
    setError('');
    try {
      await userApi.update(u.shain_code, { is_active: !u.is_active });
      setMessage(`${actionLabel}しました`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (u) => {
    if (
      !window.confirm(
        `${u.display_name}（${u.login_id}）のパスワードを再設定しますか？\n新しいパスワードが発行されます。`
      )
    ) {
      return;
    }
    setError('');
    try {
      const res = await userApi.resetPassword(u.shain_code, {});
      setMessage('パスワードを再設定しました');
      setCreatedPassword(res.password);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="page-title mb-6">ユーザー管理</h1>

      {error && <p className="text-red-600 mb-4">{error}</p>}
      {message && <p className="text-green-700 mb-2">{message}</p>}
      {createdPassword && (
        <p className="text-sm bg-amber-50 border border-amber-200 p-3 mb-4 rounded">
          発行パスワード: <strong>{createdPassword}</strong>（安全な場所に保管してください）
        </p>
      )}

      <section className="bg-white border border-slate-200 rounded-lg p-4 mb-8">
        <h2 className="text-lg font-medium mb-4">新規登録</h2>
        <form onSubmit={handleCreate} className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm text-slate-600 mb-1">社員</label>
            <select
              value={form.shain_code}
              onChange={(e) => setForm({ ...form, shain_code: e.target.value })}
              className="border rounded px-3 py-2 min-w-[200px]"
              required
            >
              <option value="">選択してください</option>
              {available.map((s) => (
                <option key={s.shain_code} value={s.shain_code}>
                  {s.login_id} - {s.display_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">権限</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="user">一般</option>
              <option value="admin">管理者</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">初期パスワード（空で自動生成）</label>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="border rounded px-3 py-2"
            />
          </div>
          <button type="submit" className="bg-slate-800 text-white px-4 py-2 rounded hover:bg-slate-700">
            登録
          </button>
        </form>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <h2 className="text-lg font-medium p-4 border-b">登録済みユーザー</h2>
        {loading ? (
          <p className="p-4 text-slate-600">読み込み中...</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3">表示コード</th>
                <th className="text-left p-3">氏名</th>
                <th className="text-left p-3">権限</th>
                <th className="text-left p-3">状態</th>
                <th className="text-left p-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.shain_code} className="border-t">
                  <td className="p-3">{u.login_id}</td>
                  <td className="p-3">{u.display_name}</td>
                  <td className="p-3">{u.role}</td>
                  <td className="p-3">{u.is_active ? '有効' : '無効'}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(u)}
                        disabled={u.shain_code === 9999}
                        className={`px-3 py-1 rounded text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                          u.is_active
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {u.is_active ? '無効化' : '有効化'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResetPassword(u)}
                        className="px-3 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                      >
                        PW再設定
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default UserManagementPage;
